import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  UserPlus,
  Cog,
  BarChart3,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  UserCheck,
  Target,
  Compass,
  TestTube2,
  Phone,
} from "lucide-react";
import PageMeta from "../site/PageMeta";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../ui/tabs";

const pillars = [
  {
    key: "rpo",
    icon: UserPlus,
    tag: "In RPO",
    heading: "AI-Assisted Recruiting",
    accent: "#00E5FF",
    intro:
      "Recruiting has the most repetitive, time-consuming steps of any function we run — and the most consequence when something gets missed. Here's where AI actually speeds things up, and where a recruiter always has the final say.",
    capabilities: [
      {
        name: "Resume parsing & matching",
        body: "AI reads and structures resumes at scale, matching candidates to role requirements based on actual skills and experience, not just keyword overlap.",
      },
      {
        name: "AI-assisted sourcing",
        body: "AI expands search queries across platforms to surface candidates who wouldn't show up in a standard keyword search.",
      },
      {
        name: "Outreach personalization",
        body: "AI drafts the first pass of candidate outreach, personalized to the role and the candidate's background — a recruiter reviews before anything sends.",
      },
      {
        name: "Shortlist scoring",
        body: "AI ranks candidates against the role brief so recruiters spend their time on the strongest matches first, not sorting the full pool by hand.",
      },
    ],
    humanLabel: "What stays human",
    humanBody:
      "Every shortlist is reviewed by a recruiter before it reaches you, and every final call is made by someone who's actually read the resume and spoken to the candidate.",
  },
  {
    key: "bpo",
    icon: Cog,
    tag: "In BPO",
    heading: "AI-Assisted Operations",
    accent: "#A3FF5E",
    intro:
      "Day-to-day operational work is full of repeatable, rules-based steps — and a few judgment calls that shouldn't be automated. We split it that way on purpose.",
    capabilities: [
      {
        name: "First-response triage",
        body: "AI reads incoming requests and routes or drafts a first response for routine cases.",
      },
      {
        name: "Data entry & document processing",
        body: "AI extracts and structures information from documents and forms, cutting manual entry time significantly.",
      },
      {
        name: "Workflow automation",
        body: "Repetitive multi-step processes — status updates, follow-ups, record syncing — run automatically once set up, instead of depending on someone remembering to do them.",
      },
      {
        name: "Quality flagging",
        body: "AI monitors patterns in volume and flags anomalies — a spike in a complaint type, an unusual delay — for a specialist to look into.",
      },
    ],
    humanLabel: "What stays human",
    humanBody:
      "Anything that isn't routine — an escalation, an unusual request, a judgment call on an account — goes to a specialist, not an autoresponder.",
  },
  {
    key: "kpo",
    icon: BarChart3,
    tag: "In KPO",
    heading: "AI-Assisted Knowledge Work",
    accent: "#007AFF",
    intro:
      "Data and research work benefits from AI differently — less about speed on repetitive tasks, more about surfacing what a person would otherwise have to dig for manually.",
    capabilities: [
      {
        name: "Data structuring & cleaning",
        body: "AI standardizes inconsistent data from multiple sources before analysis starts.",
      },
      {
        name: "Pattern & anomaly detection",
        body: "AI flags trends, outliers, and correlations in large datasets that would take an analyst far longer to find manually.",
      },
      {
        name: "Research synthesis",
        body: "AI helps compile and summarize research from multiple sources into a structured first draft, which an analyst then verifies and refines.",
      },
      {
        name: "Reporting automation",
        body: "Recurring reports and dashboards update automatically from live data instead of being rebuilt manually each cycle.",
      },
    ],
    humanLabel: "What stays human",
    humanBody:
      "Every dataset, every flagged pattern, and every report is reviewed by an analyst who understands the domain — before it becomes a recommendation you act on.",
  },
];

const buildPoints = [
  {
    icon: Compass,
    title: "Built on your stack",
    body: "We start with your existing tools and process — not a one-size-fits-all system.",
  },
  {
    icon: TestTube2,
    title: "Tested before trusted",
    body: "We stand up the AI-assisted workflow, test it against real work, and refine it before it's trusted with volume.",
  },
  {
    icon: Phone,
    title: "A name on every workflow",
    body: "Every workflow has a named specialist accountable for its output — if something's off, you have someone to call, not a support ticket.",
  },
];

function PillarPanel({ p }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-2xl border border-slate-800 bg-gradient-to-b from-[#0A1325] to-[#070F1F] overflow-hidden"
      data-testid={`ai-pillar-panel-${p.key}`}
    >
      <div
        className="absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-[0.12] blur-3xl"
        style={{ background: p.accent }}
      />

      {/* Header */}
      <div className="relative p-8 sm:p-10 border-b border-slate-800/60">
        <div className="flex items-start gap-5">
          <div
            className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border"
            style={{
              borderColor: `${p.accent}40`,
              background: `${p.accent}12`,
              color: p.accent,
            }}
          >
            <p.icon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="font-display text-[10px] font-bold tracking-[0.26em] uppercase"
              style={{ color: p.accent }}
            >
              {p.key.toUpperCase()} · Capability
            </div>
            <h3 className="font-display mt-2 text-2xl sm:text-3xl font-semibold text-white leading-tight">
              {p.heading}
            </h3>
            <p className="pull-quote mt-4 text-lg sm:text-xl leading-snug text-white/90 max-w-3xl">
              “{p.intro}”
            </p>
          </div>
        </div>
      </div>

      {/* Capabilities */}
      <div className="relative p-8 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
        {p.capabilities.map((c, i) => (
          <motion.div
            key={c.name}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            data-testid={`ai-${p.key}-capability-${i}`}
            className="rounded-xl border border-slate-800 bg-[#050B14]/60 p-5 hover:border-[color:var(--ai-accent)] transition-colors"
            style={{ "--ai-accent": `${p.accent}66` }}
          >
            <div className="flex items-start gap-3">
              <span className="chip-ai text-[9px] mt-0.5 shrink-0">AI</span>
              <div>
                <div className="font-display text-base font-semibold text-white">
                  {c.name}
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                  {c.body}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* What stays human */}
      <div className="relative px-8 sm:px-10 pb-10">
        <div className="rounded-xl border border-slate-800 bg-[#050B14]/70 p-6 flex items-start gap-4">
          <div
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border"
            style={{
              borderColor: `${p.accent}40`,
              background: `${p.accent}12`,
              color: p.accent,
            }}
          >
            <UserCheck size={18} />
          </div>
          <div>
            <div
              className="text-[10px] tracking-[0.22em] uppercase font-bold"
              style={{ color: p.accent }}
            >
              {p.humanLabel}
            </div>
            <p className="mt-2 text-base leading-relaxed text-slate-200">
              {p.humanBody}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function AIAtA4Assist() {
  return (
    <>
      <PageMeta
        title="AI at A4 Assist — Where It Actually Helps"
        description="How A4 Assist uses AI across RPO, BPO, and KPO — resume parsing, triage, data cleaning, reporting — with a specialist accountable for every output."
      />

      {/* ========== Intro (kept) ========== */}
      <section className="pt-36 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="chip-ai">AI at A4 Assist</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-white leading-[1.05] max-w-4xl">
            AI where it{" "}
            <span className="font-serif italic">actually</span> helps.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            We're not an AI vendor, and we're not pretending AI replaces the
            people doing this work. We've built AI-assisted tools into how our
            teams operate — so the repetitive parts move faster and the people
            on your account spend their time on the parts that need a human
            decision.
          </p>
        </div>
      </section>

      {/* ========== Pillars (tabs — NEW) ========== */}
      <section className="pb-20" data-testid="ai-pillars-tabs-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#00E5FF]">
                Across The Three Pillars
              </div>
              <h2 className="font-display mt-2 text-2xl sm:text-3xl font-semibold text-white leading-tight">
                Pick a pillar.{" "}
                <span className="font-serif italic">See exactly</span> where AI shows up.
              </h2>
            </div>
            <p className="text-sm text-slate-500 max-w-sm">
              Three capabilities, same operating standard: AI does the volume,
              a specialist signs off on every output.
            </p>
          </div>

          <Tabs defaultValue="rpo" className="w-full" data-testid="ai-pillar-tabs">
            <TabsList
              className="relative h-auto bg-[#0A1325]/60 border border-slate-800 rounded-xl p-1.5 gap-1 w-full sm:w-auto flex overflow-x-auto"
            >
              {pillars.map((p) => (
                <TabsTrigger
                  key={p.key}
                  value={p.key}
                  data-testid={`ai-tab-${p.key}`}
                  className="group relative inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-400 transition-all data-[state=active]:bg-[#070F1F] data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_-6px_rgba(0,229,255,0.4)]"
                >
                  <span
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md border"
                    style={{
                      borderColor: `${p.accent}40`,
                      background: `${p.accent}12`,
                      color: p.accent,
                    }}
                  >
                    <p.icon size={13} />
                  </span>
                  <span className="hidden sm:inline">{p.tag}</span>
                  <span className="sm:hidden uppercase tracking-wider text-xs">
                    {p.key}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-6">
              {pillars.map((p) => (
                <TabsContent key={p.key} value={p.key} className="mt-0">
                  <PillarPanel p={p} />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </section>

      {/* ========== How We Build These Workflows (NEW) ========== */}
      <section
        className="py-20 border-y border-slate-900 bg-[#070F1F] relative"
        data-testid="ai-build-section"
      >
        <div className="absolute inset-0 bg-radial-cyan opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#A3FF5E]">
              How We Build These Workflows
            </div>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-white leading-[1.1]">
              Built for your business —{" "}
              <span className="font-serif italic">then</span> trusted with volume.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {buildPoints.map((pt, i) => (
              <motion.div
                key={pt.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid={`ai-build-point-${i}`}
                className="rounded-xl border border-slate-800 bg-[#0A1325]/70 p-6 flex gap-4 items-start"
              >
                <div className="font-display text-xs font-semibold text-slate-500 tabular-nums pt-1 w-6 shrink-0">
                  0{i + 1}
                </div>
                <div className="flex-1">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20">
                    <pt.icon size={18} />
                  </div>
                  <h3 className="font-display mt-4 text-base font-semibold text-white">
                    {pt.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                    {pt.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Beyond delivery (kept, now its own section) ========== */}
      <section className="py-20" data-testid="ai-beyond-delivery-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="rounded-2xl border border-[#00E5FF]/25 bg-gradient-to-br from-[#0B1A33] to-[#071326] p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#00E5FF]/10 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#A3FF5E]/10 blur-3xl" />
            <div className="relative flex items-start gap-5">
              <div className="hidden sm:inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 shrink-0">
                <Sparkles size={22} />
              </div>
              <div>
                <span className="chip-ai">Beyond delivery</span>
                <p className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed max-w-3xl">
                  Beyond support and sourcing, we help clients stand up
                  AI-powered systems of their own — automated call handling,
                  CRM and lead-routing workflows — so operational work keeps
                  running between engagements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== Editorial pull-quote (kept) ========== */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="rounded-2xl border border-slate-800 bg-[#070F1F] p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-radial-cyan opacity-40" />
            <div className="relative max-w-3xl">
              <Sparkles className="text-[#00E5FF]" size={28} />
              <p className="pull-quote mt-6 text-3xl sm:text-4xl text-white/95 leading-[1.15]">
                “The result isn't ‘AI-powered service' as a slogan — it's the
                same work, done faster and more consistently, by people who
                are still accountable for it.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== Final CTA (kept) ========== */}
      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
              Want to see how this would work for your business?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
              We'll audit your current workflows and show you where AI
              implementation gives you the biggest lift.
            </p>
          </div>
          <a
            href={calendlyUrl({ pillar: "ai" })}
            {...calendlyLinkProps}
            data-testid="ai-audit-cta"
            className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
          >
            Get My Free Operations Audit
            <ArrowRight size={16} className="arrow-shift" />
          </a>
        </div>
      </section>
    </>
  );
}
